import React, { Component } from 'react';
import { TrevorIcon } from '@trevor/ui-components';
import MultiDropdown from '@/components/MultiDropdown';
import { userAction } from '@/util/helpers/ajax-helpers';
import {
  ColumnCategory,
  ColumnDropdown,
  ColumnHeader,
  ColumnHeaderContainer,
  ColumnTitle,
  ColumnTitleContainer,
} from './DatatableHeaders.styles';
import DropdownItem from '../../util/DropdownItem';
import {
  ARRAY,
  BOOL,
  DATE,
  DATETIME,
  FLOAT,
  INT,
  INTERVAL,
  JSON,
  OTHER,
  STRING,
  UUID,
} from '../../util/ColumnType';
import { dateOptions } from '../../util/enums/EnumDateTimeAggregation';
import {
  JSON_ARRAY,
  JSON_OBJECT,
  SQL_ARRAY,
} from '../../util/enums/EnumExpandType';
import ModalWhere from '../modals/ModalWhere';
import ModalOrderBy from '../modals/ModalOrderBy';
import ModalSelect from '../modals/ModalSelect';
import ModalRenameColumns from '../modals/ModalRenameColumns';
import ModalRearrangeColumns from '../modals/ModalRearrangeColumns';
import ModalAggregate from '../modals/ModalAggregate';
import AggregateOperation from '../../util/AggregateOperation';
import ModalAddColumn from '../modals/ModalAddColumn';
import { MYSQL, POSTGRES, SQLSERVER } from '../../util/enums/EnumDbType';
import HighlightFeature, {
  ACTION_CLICK_TABLE_HEADER,
  ACTION_DRILLDOWN,
  BOTTOM,
  CREATE_CHART,
  SUMMARIZE,
} from '../HighlightFeature';
import { TABLE_COLUMN_NAME } from '../../constants/test-ids';
import { isIdColumnName } from '../../util/helpers/id-helpers';

class MultidropdownHeader extends Component {
  state = {
    open: false,
  };

  componentDidMount() {
    window.addEventListener('click', this.closeDropdown);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.closeDropdown);
    clearTimeout(this._timeout);
  }

  closeDropdown = () => {
    if (this.state.open) {
      this.setState({
        open: false,
      });
    }
  };

  toggleOpen() {
    const open = !this.state.open;
    // NOTE: we use a timeout here so that it happens *after* all the existing open dropdowns are closed
    this._timeout = setTimeout(() => {
      this.setState({
        open,
      });
    }, 1);
    if (open) {
      userAction(ACTION_CLICK_TABLE_HEADER);
    }
  }

  jsonItems(heading, optionClicked) {
    if (heading.columnType().type() !== JSON) {
      return null;
    }
    const columnName = heading.columnName();
    return DropdownItem.dropdown('JSON', [
      DropdownItem.action(
        <span>
          <b>access</b> nth JSON array element
        </span>,
        optionClicked(ModalAddColumn, null, {
          name: `${columnName.name()}_element`,
          expression: `${columnName.toString()}.at(0)`,
        }),
      ),
      DropdownItem.action(
        <span>
          <b>access</b> JSON array size
        </span>,
        optionClicked(ModalAddColumn, null, {
          name: `${columnName.name()}_element`,
          expression: `${columnName.toString()}.length()`,
        }),
      ),
      DropdownItem.action(
        <span>
          <b>access</b> JSON value by key
        </span>,
        optionClicked(ModalAddColumn, null, {
          name: `${columnName.name()}_item`,
          expression: `${columnName.toString()}.key("name-of-key").toString()`,
        }),
      ),
      DropdownItem.divider(),
      DropdownItem.action(
        <span>
          <b>expand</b> JSON array
        </span>,
        optionClicked(JSON_ARRAY, columnName),
      ),
      DropdownItem.action(
        <span>
          <b>expand</b> JSON object
        </span>,
        optionClicked(JSON_OBJECT, columnName),
      ),
      DropdownItem.divider(),
      DropdownItem.action(
        <span>
          <b>convert</b> to string
        </span>,
        optionClicked(ModalAddColumn, null, {
          name: `${columnName.name()}_str`,
          expression: `${columnName.toString()}.toString()`,
        }),
      ),
      DropdownItem.action(
        <span>
          <b>convert</b> to integer
        </span>,
        optionClicked(ModalAddColumn, null, {
          name: `${columnName.name()}_str`,
          expression: `${columnName.toString()}.toString().toInt()`,
        }),
      ),
      DropdownItem.divider(),
      DropdownItem.action(
        <span>
          <b>check</b> JSON is null
        </span>,
        optionClicked(ModalAddColumn, null, {
          name: `${columnName.name()}_element`,
          expression: `${columnName.toString()}.isNull()`,
        }),
      ),
    ]);
  }

  arrayItems(heading, optionClicked) {
    if (heading.columnType().type() !== ARRAY) {
      return null;
    }
    const columnName = heading.columnName();
    return DropdownItem.dropdown('ARRAY', [
      this.buildOption(
        optionClicked,
        'access array element',
        `${columnName.name()}_element`,
        `${columnName.toString()}.at(0)`,
      ),
      this.buildOption(
        optionClicked,
        'array length',
        `${columnName.name()}_length`,
        `${columnName.toString()}.length()`,
      ),
      this.buildOption(
        optionClicked,
        'convert to text string',
        `${columnName.name()}_str`,
        `${columnName.toString()}.toString()`,
      ),
      DropdownItem.action(
        <span>
          <b>expand</b> SQL array
        </span>,
        optionClicked(SQL_ARRAY, columnName),
      ),
    ]);
  }

  rowsItems(heading, optionClicked) {
    if ([ARRAY, OTHER].includes(heading.columnType().type())) {
      return [];
    }
    const items = [];

    items.push(
      DropdownItem.action(
        <span>sort</span>,
        optionClicked(ModalOrderBy, heading.columnName()),
      ),
      DropdownItem.action(
        <span>filter</span>,
        optionClicked(ModalWhere, heading.columnName()),
      ),
    );
    // }
    return items;
  }

  buildOption(optionClicked, label, newColumnName, expression) {
    return DropdownItem.action(
      label,
      optionClicked(ModalAddColumn, null, {
        name: newColumnName,
        expression,
      }),
    );
  }

  columnItems(heading, optionClicked, dbType) {
    const columnName = heading.columnName();
    const convert = [];
    const formatAs = [];
    const extract = [];
    const logic = [];
    const math = [];
    switch (heading.columnType().type()) {
      case BOOL:
        logic.push(
          this.buildOption(
            optionClicked,
            'if (TRUE) then ... else ...',
            `${columnName.name()}_if`,
            `if (${columnName.toString()}) then "It is true" else "It is false"`,
          ),
        );
        logic.push(
          this.buildOption(
            optionClicked,
            'not (TRUE)',
            `not_${columnName.name()}`,
            `${columnName.toString()}.isFalse()`,
          ),
        );
        break;
      case INT:
        formatAs.push(
          this.buildOption(
            optionClicked,
            'comma-separated: 2,399',
            `${columnName.name()}_pretty`,
            `${columnName.toString()}.pretty()`,
            true,
            'Format the number',
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            '+ n',
            `${columnName.name()}_math`,
            `${columnName.toString()} + 1`,
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            '- n',
            `${columnName.name()}_maths`,
            `${columnName.toString()} - 1`,
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            '* n',
            `${columnName.name()}_math_`,
            `${columnName.toString()} * 1`,
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            '/ n',
            `${columnName.name()}_maths_`,
            `${columnName.toString()} / 1.0`,
          ),
        );
        convert.push(
          this.buildOption(
            optionClicked,
            'convert to text',
            `${columnName.name()}_str`,
            `${columnName.toString()}.toString()`,
          ),
        );
        convert.push(
          this.buildOption(
            optionClicked,
            'UNIX timestamp to DateTime',
            `${columnName.name()}_ts`,
            `date("1970-01-01").toDateTime() + ${columnName.toString()}.seconds()`,
          ),
        );
        if (heading.columnType().nullable()) {
          convert.push(
            this.buildOption(
              optionClicked,
              'convert NULLs to 0',
              `${columnName.name()}_or_0`,
              `${columnName.toString()}.ifNull(0)`,
            ),
          );
        }
        break;
      case FLOAT:
        formatAs.push(
          this.buildOption(
            optionClicked,
            'comma-separated: 2,399.99',
            `${columnName.name()}_pretty`,
            `${columnName.toString()}.pretty()`,
            true,
            'Format the number',
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            '+ n',
            `${columnName.name()}_math`,
            `${columnName.toString()} + 1.0`,
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            '- n',
            `${columnName.name()}_maths`,
            `${columnName.toString()} - 1.0`,
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            '* n',
            `${columnName.name()}_math_`,
            `${columnName.toString()} * 1.0`,
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            '/ n',
            `${columnName.name()}_maths_`,
            `${columnName.toString()} / 1.0`,
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            'round',
            `${columnName.name()}_rounded`,
            `${columnName.toString()}.round()`,
          ),
        );
        if (heading.columnType().nullable()) {
          convert.push(
            this.buildOption(
              optionClicked,
              'convert NULLs to 0.0',
              `${columnName.name()}_or_0`,
              `${columnName.toString()}.ifNull(0.0)`,
            ),
          );
        }
        break;
      case DATE:
      case DATETIME:
        convert.push(
          this.buildOption(
            optionClicked,
            'convert to London timezone',
            `${columnName.name()}_ln`,
            dbType === SQLSERVER
              ? `${columnName.toString()}.toTimeZone("GMT Standard Time")`
              : `${columnName.toString()}.toTimeZone("Europe/London")`,
            true,
            'Convert UTC time to LN time',
          ),
        );
        convert.push(
          this.buildOption(
            optionClicked,
            'convert to New York timezone',
            `${columnName.name()}_ny`,
            dbType === SQLSERVER
              ? `${columnName.toString()}.toTimeZone("Eastern Standard Time")`
              : `${columnName.toString()}.toTimeZone("America/New_York")`,
            true,
            'Convert UTC time to NY time',
          ),
        );
        convert.push(
          this.buildOption(
            optionClicked,
            'convert to Los Angeles timezone',
            `${columnName.name()}_la`,
            dbType === SQLSERVER
              ? `${columnName.toString()}.toTimeZone("Pacific Standard Time")`
              : `${columnName.toString()}.toTimeZone("America/Los_Angeles")`,
            true,
            'Convert UTC time to LA time',
          ),
        );

        if (dbType === MYSQL) {
          formatAs.push(
            this.buildOption(
              optionClicked,
              'Mon 27 Jan 2020',
              `${columnName.name()}_formatted`,
              `${columnName.toString()}.format("%a %d %b %Y")`,
              true,
              'Format the date',
            ),
          );
          formatAs.push(
            this.buildOption(
              optionClicked,
              '27 January 2020',
              `${columnName.name()}_formatted`,
              `${columnName.toString()}.format("%d %M %Y")`,
              true,
              'Format the date',
            ),
          );
          formatAs.push(
            this.buildOption(
              optionClicked,
              '27/01/2020',
              `${columnName.name()}_formatted`,
              `${columnName.toString()}.format("%d/%m/%Y")`,
              true,
              'Format the date',
            ),
          );
          formatAs.push(
            this.buildOption(
              optionClicked,
              '01/27/2020',
              `${columnName.name()}_formatted`,
              `${columnName.toString()}.format("%m/%d/%Y")`,
              true,
              'Format the date',
            ),
          );
          formatAs.push(
            this.buildOption(
              optionClicked,
              '2020-01-27 14:48',
              `${columnName.name()}_formatted`,
              `${columnName.toString()}.format("%Y-%m-%d %H:%i")`,
              true,
              'Format the date',
            ),
          );
        } else if (dbType === POSTGRES) {
          formatAs.push(
            this.buildOption(
              optionClicked,
              'Mon 27 Jan 2020',
              `${columnName.name()}_formatted`,
              `${columnName.toString()}.format("Dy DD Mon YYYY")`,
              true,
              'Format the date',
            ),
          );
          formatAs.push(
            this.buildOption(
              optionClicked,
              '27 January 2020',
              `${columnName.name()}_formatted`,
              `${columnName.toString()}.format("DD Month YYYY")`,
              true,
              'Format the date',
            ),
          );
          formatAs.push(
            this.buildOption(
              optionClicked,
              '27/01/2020',
              `${columnName.name()}_formatted`,
              `${columnName.toString()}.format("DD/MM/YYYY")`,
              true,
              'Format the date',
            ),
          );
          formatAs.push(
            this.buildOption(
              optionClicked,
              '01/27/2020',
              `${columnName.name()}_formatted`,
              `${columnName.toString()}.format("MM/DD/YYYY")`,
              true,
              'Format the date',
            ),
          );
          formatAs.push(
            this.buildOption(
              optionClicked,
              '2020-01-27 14:48',
              `${columnName.name()}_formatted`,
              `${columnName.toString()}.format("YYYY-MM-DD HH24:MI")`,
              true,
              'Format the date',
            ),
          );
        }
        // formatAs.push(this.buildOption(optionClicked,
        // 	'year-month-day-hour-min',
        // 	columnName.name()+'_date_hour_min',
        // 	columnName.toString()+'.toString().truncate(16)',
        // 	true,
        // 	'Extract the day, hour and minute and add it to a new column'
        // ));
        // formatAs.push(this.buildOption(optionClicked,
        // 	'year-month-day-hour',
        // 	columnName.name()+'_date_hour',
        // 	columnName.toString()+'.toString().truncate(13)+":00"',
        // 	true,
        // 	'Extract the day and hour and add it to a new column'
        // ));
        // formatAs.push(this.buildOption(optionClicked,
        // 	'year-month-day',
        // 	columnName.name()+'_day',
        // 	columnName.toString()+'.toDate()',
        // 	true,
        // 	'Extract the day and add it to a new column'
        // ));
        // formatAs.push(this.buildOption(optionClicked,
        // 	'year-month',
        // 	columnName.name()+'_yr_month',
        // 	columnName.toString()+'.startOfMonth().toDate()',
        // 	true,
        // 	'Extract the year and month and add it to a new column'
        // ));
        extract.push(
          this.buildOption(
            optionClicked,
            'second',
            `${columnName.name()}_second`,
            `${columnName.toString()}.second()`,
            true,
            'Extract the second and add it to a new column',
          ),
        );
        extract.push(
          this.buildOption(
            optionClicked,
            'minute',
            `${columnName.name()}_minute`,
            `${columnName.toString()}.minute()`,
            true,
            'Extract the minute and add it to a new column',
          ),
        );
        extract.push(
          this.buildOption(
            optionClicked,
            'hour',
            `${columnName.name()}_hour`,
            `${columnName.toString()}.hour()`,
            true,
            'Extract the hour and add it to a new column',
          ),
        );
        extract.push(
          this.buildOption(
            optionClicked,
            'date',
            `${columnName.name()}_date`,
            `${columnName.toString()}.date()`,
            true,
            'Extract the date and add it to a new column',
          ),
        );
        extract.push(
          this.buildOption(
            optionClicked,
            'week',
            `${columnName.name()}_week`,
            `${columnName.toString()}.startOfWeek().toDate()`,
            true,
            'Extract the week and add it to a new column',
          ),
        );
        extract.push(
          this.buildOption(
            optionClicked,
            'day of week',
            `${columnName.name()}_day_of_week`,
            `${columnName.toString()}.weekday()`,
            true,
            'Get the day of the week',
          ),
        );
        extract.push(
          this.buildOption(
            optionClicked,
            'month',
            `${columnName.name()}_month`,
            `${columnName.toString()}.month()`,
            true,
            'Extract the month and add it to a new column',
          ),
        );
        extract.push(
          this.buildOption(
            optionClicked,
            'quarter',
            `${columnName.name()}_quarter`,
            `${columnName.toString()}.year() +" q"+ (((${columnName.toString()}.month()-1) / 3).floor() +1)`,
            true,
            'Extract the quarter and add it to a new column',
          ),
        );
        extract.push(
          this.buildOption(
            optionClicked,
            'year',
            `${columnName.name()}_year`,
            `${columnName.toString()}.year()`,
            true,
            'Extract the year and add it to a new column',
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            `now() - ${columnName.name()}`,
            `${columnName.name()}_age`,
            `(now() - ${columnName.toString()}.toDateTime() ).toSeconds()`,
            true,
            'Extract the age in years and add it to a new column',
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            'seconds since 1970',
            `${columnName.name()}_secs_since_1970`,
            `${columnName.toString()}.secondsSinceEpoch()`,
            true,
            'Useful for comparing two different timestamp columns',
          ),
        );
        if (heading.columnType().type() === DATE) {
          break;
        }
        math.push(
          this.buildOption(
            optionClicked,
            'add 1 hour',
            `${columnName.name()}_adjusted`,
            `(${columnName.toString()} + 1.hour())`,
          ),
        );
        math.push(
          this.buildOption(
            optionClicked,
            'adjust by 8 hours',
            `${columnName.name()}_local`,
            `(${columnName.toString()} - 8.hours())`,
          ),
        );
        if (dbType !== MYSQL) {
          //
          // MySQL can't do intervals
          //
          math.push(
            this.buildOption(
              optionClicked,
              'age in years',
              `${columnName.name()}_years_ago`,
              `(now() - ${columnName.toString()}).getYears()`,
            ),
          );
          math.push(
            this.buildOption(
              optionClicked,
              'number of days ago',
              `${columnName.name()}_days_ago`,
              `((now() - ${columnName.toString()}).toSeconds() / 60 / 60 / 24).round()`,
            ),
          );
          if (columnName.hasAlias()) {
            math.push(
              this.buildOption(
                optionClicked,
                `${columnName.name()} - created_at`,
                'duration_seconds',
                `(${columnName.toString()} - ${columnName.alias()}.created_at).toSeconds()`,
              ),
            );
          }
        }
        logic.push(
          this.buildOption(
            optionClicked,
            `if ${columnName.toString()} is within last hour then true`,
            'within_last_hour',
            `${columnName.toString()}.toDateTime() > (now() - 1.hour())`,
          ),
        );
        logic.push(
          this.buildOption(
            optionClicked,
            `if ${columnName.toString()} is after date then true`,
            'is_after_date',
            `${columnName.toString()}.toDateTime() > date("2019-01-01").toDateTime()`,
          ),
        );
        break;
      case STRING:
        formatAs.push(
          this.buildOption(
            optionClicked,
            'add text',
            `hello_${columnName.name()}`,
            `"hello "+${columnName.toString()}`,
          ),
        );
        formatAs.push(
          this.buildOption(
            optionClicked,
            'find and replace characters',
            `${columnName.name()}_replaced`,
            `${columnName.toString()}.replace(" ", "_")`,
          ),
        );
        formatAs.push(
          this.buildOption(
            optionClicked,
            'format as URL',
            `${columnName.name()}_URL`,
            `"https://your-website.com/"+${columnName.toString()}`,
          ),
        );
        convert.push(
          this.buildOption(
            optionClicked,
            'convert to number',
            `${columnName.name()}_int`,
            `${columnName.toString()}.toInt()`,
          ),
        );
        convert.push(
          this.buildOption(
            optionClicked,
            'convert to decimal',
            `${columnName.name()}_float`,
            `${columnName.toString()}.toFloat()`,
          ),
        );
        convert.push(
          this.buildOption(
            optionClicked,
            'convert to date',
            `${columnName.name()}_date`,
            `${columnName.toString()}.toDate()`,
          ),
        );
        logic.push(
          this.buildOption(
            optionClicked,
            `if ${columnName.name()} equals "value" then x else y`,
            `${columnName.name()}_if_then`,
            `if ${columnName.toString()} === "some_value" then "x" else "y"`,
          ),
        );
        logic.push(
          this.buildOption(
            optionClicked,
            `if ${columnName.name()} contains "value" return true`,
            `${columnName.name()}_contains`,
            `${columnName.toString()}.contains("value")`,
          ),
        );
        if (dbType === POSTGRES) {
          convert.push(
            this.buildOption(
              optionClicked,
              'convert to JSON',
              `${columnName.name()}_json`,
              `${columnName.toString()}.toJson()`,
            ),
          );
        }
        extract.push(
          this.buildOption(
            optionClicked,
            'extract domain from email',
            'domain',
            dbType !== MYSQL && dbType !== SQLSERVER
              ? `${columnName.toString()}.extract("@(.*)")`
              : `${columnName.toString()}.substring(${columnName.toString()}.position("@")+1)`,
          ),
        );

        extract.push(
          this.buildOption(
            optionClicked,
            'extract characters before a space',
            'characters_before_space',
            dbType !== MYSQL && dbType !== SQLSERVER
              ? `${columnName.toString()}.extract("(.*) ")`
              : `${columnName.toString()}.truncate(${columnName.toString()}.position(" "))`,
          ),
        );

        formatAs.push(
          this.buildOption(
            optionClicked,
            'convert to lowercase',
            `${columnName.name()}_lower`,
            `${columnName.toString()}.lowercase()`,
          ),
        );
        break;
      case INTERVAL:
        convert.push(
          this.buildOption(
            optionClicked,
            'as seconds',
            `${columnName.name()}_secs`,
            `${columnName.toString()}.toSeconds()`,
          ),
        );
        convert.push(
          this.buildOption(
            optionClicked,
            'as minutes',
            `${columnName.name()}_mins`,
            `${columnName.toString()}.toSeconds() / 60`,
          ),
        );
        convert.push(
          this.buildOption(
            optionClicked,
            'as hours',
            `${columnName.name()}_hours`,
            `${columnName.toString()}.toSeconds() / (60*60)`,
          ),
        );
        convert.push(
          this.buildOption(
            optionClicked,
            'as days',
            `${columnName.name()}_days`,
            `${columnName.toString()}.toSeconds() / (60*60*24)`,
          ),
        );
        convert.push(
          this.buildOption(
            optionClicked,
            'as weeks',
            `${columnName.name()}_weeks`,
            `${columnName.toString()}.toSeconds() / (60*60*24*7)`,
          ),
        );
        break;
      case UUID:
        convert.push(
          this.buildOption(
            optionClicked,
            'convert to text',
            `${columnName.name()}_str`,
            `${columnName.toString()}.toString()`,
          ),
        );
        break;
      case OTHER:
        convert.push(
          this.buildOption(
            optionClicked,
            'convert to text',
            `${columnName.name()}_str`,
            `${columnName.toString()}.toString()`,
          ),
        );
        break;
    }
    if (heading.columnType().nullable()) {
      logic.push(
        this.buildOption(
          optionClicked,
          'if null then 0 else 1',
          `${columnName.name()}_null_or_not`,
          `if ( ${columnName.toString()}.isNull() ) then 0 else 1`,
        ),
      );
      logic.push(
        this.buildOption(
          optionClicked,
          'if null then <some-other-value>',
          `${columnName.name()}_not_null`,
          `${columnName.toString()}.ifNull(SOME_OTHER_VALUE)`,
        ),
      );
    }

    return DropdownItem.dropdown('column', [
      DropdownItem.action(
        <span>
          <b>hide</b> column
        </span>,
        optionClicked(ModalSelect, columnName),
      ),
      DropdownItem.action(
        <span>
          <b>hide</b> other columns
        </span>,
        optionClicked(ModalSelect, columnName, { invertSelection: true }),
      ),
      DropdownItem.divider(),
      DropdownItem.action(
        <span>
          <b>rename</b> column
        </span>,
        optionClicked(ModalRenameColumns, columnName),
      ),
      DropdownItem.action(
        <span>
          <b>reorder</b> columns
        </span>,
        optionClicked(ModalRearrangeColumns),
      ),
      DropdownItem.divider(),
      this.buildOption(
        optionClicked,
        'new column',
        `${columnName.name()}_new`,
        columnName.toString(),
      ),
      DropdownItem.dropdown('convert', convert),
      DropdownItem.dropdown('format', formatAs),
      DropdownItem.dropdown('math', math),
      DropdownItem.dropdown('extract', extract),
      DropdownItem.dropdown('logic', logic),
    ]);
  }

  isIdColumnHeading(heading) {
    return isIdColumnName(heading.columnName().name());
  }

  summarizeItems(heading, optionClicked) {
    if (heading.columnType().type() === JSON) {
      return null;
    }
    const columnName = heading.columnName();
    let countOptions = [{ percent: false }];
    if (
      heading.columnType().type() === DATE ||
      heading.columnType().type() === DATETIME
    ) {
      countOptions = dateOptions.map((agg) => ({
        percent: false,
        aggregation: agg,
      }));
    }

    const operations = AggregateOperation.all(this.props.dbType)
      .filter(
        (ao) =>
          ao
            .supportedTypes()
            .findIndex((t) => t === heading.columnType().type()) > -1,
      )
      .filter((ao) => !ao.isAdvanced())
      .filter((ao) =>
        this.isIdColumnHeading(heading)
          ? ao.name() !== 'sum' && ao.name() !== 'avg'
          : true,
      );

    return DropdownItem.dropdown('summarize', [
      ...countOptions.map((co) =>
        DropdownItem.action(
          <span>
            <b>count</b> by {heading.columnName().name()}{' '}
            <b>{co.aggregation ? co.aggregation : ''}</b>
          </span>,
          optionClicked(ModalAggregate, columnName, co),
        ),
      ),
      operations.length === 0 ? null : DropdownItem.divider(),
      ...operations.map((o) =>
        DropdownItem.action(
          <span>
            <b>{o.name()}</b> of {heading.columnName().name()}
          </span>,
          optionClicked(ModalAggregate, null, {
            selectedOp: o,
            columnName,
          }),
        ),
      ),
    ]);
  }

  items(heading, optionClicked, dbType) {
    return [
      ...this.rowsItems(heading, optionClicked),
      this.jsonItems(heading, optionClicked),
      this.arrayItems(heading, optionClicked),
      this.summarizeItems(heading, optionClicked, dbType),
      this.columnItems(heading, optionClicked, dbType),
    ];
  }

  render() {
    const { heading, tov, optionClicked, dbType, showOnboarding } = this.props;

    const items = this.items(heading, optionClicked, dbType);
    const classNameCSS = heading._cssClass ?? '';

    return (
      <ColumnHeader
        data-test-id={`${TABLE_COLUMN_NAME}:${heading.columnName().name()}`}
        className={`mdTableHeader ${classNameCSS}`}
      >
        <ColumnHeaderContainer>
          {showOnboarding ? (
            <HighlightFeature
              position={BOTTOM}
              text={
                <span>
                  Click the <b>headers</b> to add <b>column-specific</b>{' '}
                  <b>filter</b>, <b>formatting</b>, and <b>other</b> query
                  steps.
                </span>
              }
              hideWhenModalsOpen
              targetAction={ACTION_CLICK_TABLE_HEADER}
              style={{ minWidth: '305px' }}
              requiredActions={[SUMMARIZE, ACTION_DRILLDOWN, CREATE_CHART]}
              delay={3000}
            />
          ) : null}
          <ColumnCategory>{tov?.name?.() ?? null}</ColumnCategory>
          <ColumnTitleContainer>
            <ColumnTitle>
              <span>{heading.columnName().name()}</span>
            </ColumnTitle>
            <ColumnDropdown
              data-test-id={`${TABLE_COLUMN_NAME}:${heading
                .columnName()
                .name()}:options`}
              onClick={(e) => this.toggleOpen(e)}
            >
              <TrevorIcon name="chevron-down" />
            </ColumnDropdown>
          </ColumnTitleContainer>
        </ColumnHeaderContainer>
        {this.state.open && <MultiDropdown alwaysDown items={items} />}
      </ColumnHeader>
    );
  }
}

export default MultidropdownHeader;
